import { useHandler } from "@chatbotgang/etude/react/useHandler";
import KeyboardDoubleArrowLeftOutlined from "@mui/icons-material/KeyboardDoubleArrowLeftOutlined";
import KeyboardDoubleArrowRightOutlined from "@mui/icons-material/KeyboardDoubleArrowRightOutlined";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { LOCAL_STORAGE_SIDEBAR_COLLAPSED } from "@/appConstant";
import { AccountMenu } from "@/layout/MainContent/AccountMenu";
import { DrawerMenuList } from "@/layout/MainContent/DrawMenuList";
import { ProductSwitcher } from "@/layout/MainContent/ProductSwitcher";

const EXPANDED_WIDTH = "248px";
const COLLAPSED_WIDTH = "72px";

const styles = {
  root: {
    position: "sticky",
    top: "0px",
    alignSelf: "flex-start",
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    backgroundColor: theme.colors.white,
    borderRight: `1px solid ${theme.colors.staticFgLine}`,
    transition: "width 200ms cubic-bezier(.2, .7, .2, 1)",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    justifyContent: "center",
    padding: "12px",
  },
  menu: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "8px 12px 12px",
    borderTop: `1px solid ${theme.colors.staticFgLine}`,
  },
  compactFooter: {
    alignItems: "center",
    padding: "8px 0px 12px",
  },
  collapseButton: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    height: "32px",
    padding: "0px 12px",
    borderRadius: "6px",
    color: theme.colors.staticFgNote,
    fontSize: "0.8125rem",
    "&:hover": {
      backgroundColor: theme.colors.neutral010,
    },
    "& svg": {
      fontSize: "1.125rem",
    },
  },
  compactCollapseButton: {
    justifyContent: "center",
    width: "36px",
    height: "36px",
    padding: "0px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

// The collapsed state is a per-browser convenience; storage can be missing or
// blocked, so it falls back to expanded.
const readCollapsed = (): boolean => {
  try {
    return localStorage.getItem(LOCAL_STORAGE_SIDEBAR_COLLAPSED) === "true";
  } catch {
    return false;
  }
};

const writeCollapsed = (collapsed: boolean) => {
  try {
    localStorage.setItem(LOCAL_STORAGE_SIDEBAR_COLLAPSED, String(collapsed));
  } catch {
    // Not remembered across visits; the toggle still works for this one.
  }
};

interface SidebarProps {
  organizationName: string | undefined;
}

/**
 * The platform's one side menu: product and org at the top, every page in
 * its group, and the signed-in account at the bottom. It collapses to icons.
 */
export const Sidebar: React.FC<SidebarProps> = function Sidebar({
  organizationName,
}) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggle = useHandler(() => {
    setCollapsed((previous) => {
      writeCollapsed(!previous);
      return !previous;
    });
  });

  const toggleLabel = collapsed
    ? t("sideMenu.expand", "Expand")
    : t("sideMenu.collapse", "Collapse");

  return (
    <Box
      component="nav"
      aria-label={t("sideMenu.label", "Main")}
      sx={{
        ...styles.root,
        width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
        flex: `0 0 ${collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH}`,
      }}
    >
      <Box sx={styles.header}>
        <ProductSwitcher
          organizationName={organizationName}
          compact={collapsed}
        />
      </Box>
      <Box sx={styles.menu}>
        <DrawerMenuList compact={collapsed} />
      </Box>
      <Box sx={{ ...styles.footer, ...(collapsed && styles.compactFooter) }}>
        <ButtonBase
          onClick={toggle}
          aria-label={toggleLabel}
          aria-expanded={!collapsed}
          sx={{
            ...styles.collapseButton,
            ...(collapsed && styles.compactCollapseButton),
          }}
        >
          {collapsed ? (
            <KeyboardDoubleArrowRightOutlined />
          ) : (
            <>
              <KeyboardDoubleArrowLeftOutlined />
              {toggleLabel}
            </>
          )}
        </ButtonBase>
        <AccountMenu compact={collapsed} />
      </Box>
    </Box>
  );
};
